/**
 * Broker sessions opened through an external identity provider (Alptis Connect).
 *
 * The gateway hands the token over in the URL fragment of the landing page, bound to the browser
 * tab that started the login. Stored in `localStorage` so every tab shares the account's one live
 * session (a new login revokes the previous one server-side). Distinct from the impersonation
 * key on purpose: a federated session is the broker's own, with no staff banner.
 */

const TOKEN_KEY = 'korint.federated-session.token';
const SIGNED_OUT_KEY = 'korint.federated-session.signed-out';
const RENEWAL_LEASE_KEY = 'korint.federated-session.renewing';
const BINDING_KEY = 'korint.federated-session.binding';
const LAST_RENEWAL_KEY = 'korint.federated-session.last-renewal';

export const FEDERATED_SESSION_FRAGMENT_KEY = 'federatedSessionToken';

/** Stamped on axios configs whose bearer came from a federated session. */
export const FEDERATED_REQUEST_FLAG = '_korintFederatedRequest' as const;

/** Storage throws instead of returning null when blocked (Safari private mode, quotas). */
const read = (storage: () => Storage, key: string): string | null => {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
};

const write = (storage: () => Storage, key: string, value: string): boolean => {
  try {
    storage().setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

const remove = (storage: () => Storage, key: string): void => {
  try {
    storage().removeItem(key);
  } catch {
    // Nothing was stored if storage is unavailable.
  }
};

const local = () => window.localStorage;
const session = () => window.sessionStorage;

export type FederatedSession = {
  readonly token: string;
  readonly sessionId: string;
  readonly brokerUserId: string;
  readonly binding: string;
  readonly expiresAt: Date;
};

const decodePayload = (token: string): Record<string, unknown> | null => {
  const segment = token.split('.')[1];
  if (!segment) return null;
  try {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const bytes = Uint8Array.from(atob(padded), character => character.charCodeAt(0));
    const payload: unknown = JSON.parse(new TextDecoder().decode(bytes));
    return typeof payload === 'object' && payload !== null
      ? (payload as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

/** Display and routing claims only: the gateway validates signature, tenant and revocation. */
export const parseFederatedSessionToken = (token: string): FederatedSession | null => {
  const payload = decodePayload(token);
  if (!payload) return null;
  const { jti, sub, bnd, exp } = payload;
  if (
    typeof jti !== 'string' ||
    typeof sub !== 'string' ||
    typeof bnd !== 'string' ||
    !Number.isFinite(exp)
  ) {
    return null;
  }
  return {
    token,
    sessionId: jti,
    brokerUserId: sub,
    binding: bnd,
    expiresAt: new Date((exp as number) * 1000),
  };
};

/**
 * The stored session, expired or not: the gateway decides, and its 401 goes through the one
 * loop-protected renewal path, so a skewed local clock can't cause a login loop.
 */
export const readFederatedSession = (): FederatedSession | null => {
  const token = read(local, TOKEN_KEY);
  return token ? parseFederatedSessionToken(token) : null;
};

export const storeFederatedSession = (token: string): boolean => {
  remove(local, SIGNED_OUT_KEY);
  remove(local, RENEWAL_LEASE_KEY);
  return write(local, TOKEN_KEY, token);
};

/** Ends the session in every tab; the signed-out marker stops them logging back in silently. */
export const markFederatedSessionSignedOut = (): void => {
  write(local, SIGNED_OUT_KEY, new Date().toISOString());
  remove(local, TOKEN_KEY);
};

export const isFederatedSessionSignedOut = (): boolean => read(local, SIGNED_OUT_KEY) !== null;

export const FEDERATED_SESSION_STORAGE_KEY = TOKEN_KEY;
export const FEDERATED_SIGNED_OUT_STORAGE_KEY = SIGNED_OUT_KEY;

const randomBinding = (): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(32));
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

/** A fresh single-use value for this tab; the session token must carry it back. */
export const createLoginBinding = (): string | null => {
  const binding = randomBinding();
  return write(session, BINDING_KEY, binding) ? binding : null;
};

/** Reads and forgets the tab's binding: each one serves a single login. */
export const takeLoginBinding = (): string | null => {
  const binding = read(session, BINDING_KEY);
  remove(session, BINDING_KEY);
  return binding;
};

/** Another tab is already renewing: wait for its token instead of logging in again. */
const RENEWAL_LEASE_MS = 15_000;
/** A second renewal this soon after the previous one, with no successful call between, is a loop. */
const RENEWAL_LOOP_MS = 60_000;

export const isRenewalLeasedByAnotherTab = (now: number = Date.now()): boolean => {
  const startedAt = Number(read(local, RENEWAL_LEASE_KEY));
  return Number.isFinite(startedAt) && startedAt > 0 && now - startedAt < RENEWAL_LEASE_MS;
};

export const isRenewalLooping = (now: number = Date.now()): boolean => {
  const startedAt = Number(read(session, LAST_RENEWAL_KEY));
  return Number.isFinite(startedAt) && startedAt > 0 && now - startedAt < RENEWAL_LOOP_MS;
};

export const recordRenewal = (now: number = Date.now()): void => {
  write(local, RENEWAL_LEASE_KEY, String(now));
  write(session, LAST_RENEWAL_KEY, String(now));
};

/** Any successful federated call proves the session works, so the next renewal is legitimate. */
export const recordFederatedSuccess = (): void => remove(session, LAST_RENEWAL_KEY);

export const readFederatedTokenFromFragment = (hash: string): string | null =>
  new URLSearchParams(hash.replace(/^#/, '')).get(FEDERATED_SESSION_FRAGMENT_KEY);
